import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";
import { User } from "../types/models";
import { apiFetch, API_BASE_URL } from "../api/http";

interface IAuthContext {
  user: User | null;
  isLoading: boolean;
  login: () => void;
  logout: () => Promise<void>;
  refetchUser: () => void;
}
const AuthContext = createContext<IAuthContext | null>(null);

export const AuthProvider = ({
  children,
  setPage,
}: {
  children: React.ReactNode;
  setPage: (page: string) => void;
}) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refetchUser = useCallback(async () => {
    setIsLoading(true);
    try {
      const res = await apiFetch("/api/auth/me");
      const fetchedUser = res.ok ? await res.json() : null;
      setUser(fetchedUser);

      // ✅ Detect first load after Google OAuth redirect
      if (localStorage.getItem("justLoggedIn") && fetchedUser) {
        localStorage.removeItem("justLoggedIn");
        if (fetchedUser.role === "Manager") setPage("manager");
        else setPage("cashier");
      }
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  }, [setPage]);

  useEffect(() => {
    refetchUser();
  }, [refetchUser]);

  const login = () => {
    setIsLoading(true);
    localStorage.removeItem("momtea.page");
    localStorage.removeItem("momtea.tab");
    localStorage.removeItem("kiosk.highContrast");
    localStorage.removeItem("kiosk.activeCategory");
    localStorage.setItem("justLoggedIn", "true");
    window.location.href = `${API_BASE_URL}/api/auth/login`;
  };

  const logout = async () => {
    await apiFetch("/api/auth/logout");
    setUser(null);
    localStorage.removeItem("momtea.page");
    localStorage.removeItem("momtea.tab");
    localStorage.removeItem("kiosk.highContrast");
    localStorage.removeItem("justLoggedIn");
    localStorage.removeItem("kiosk.activeCategory")
    window.location.reload();
  };

  return (
    <AuthContext.Provider
      value={{ user, isLoading, login, logout, refetchUser }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};
